import { icons } from '../icons';
import React, { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';

type KoobModalProps = {
  open: boolean;
  setOpen?: (open: boolean) => void;
  title?: React.ReactNode;
  widthClass?: string;
  paddingClass?: string;
  overflow?: 'hidden' | 'visible';
  children?: React.ReactNode;
  allowClose?: boolean;
  showCloseIcon?: boolean;
  autoFocus?: boolean;
  initialFocus?: React.MutableRefObject<HTMLElement | null>;
};

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

export default function KoobModal({
  open,
  setOpen,
  // Renders the standard dialog heading. Prefer it over hand-rolling one at
  // the call site — that is how modal titles drifted to three different sizes.
  title,
  widthClass = 'sm:max-w-xl',
  // Only override for full-bleed content ('p-0'); a titled modal needs the
  // default padding to sit right.
  paddingClass = 'px-4 pt-5 pb-4 sm:p-6',
  overflow = 'hidden',
  children,
  allowClose = true,
  // Opt-in: call sites that render their own header already have a close
  // affordance, so a second icon would double up with theirs. A titled modal
  // gets one by default — every closable modal must have one.
  showCloseIcon = Boolean(title),
  autoFocus = true,
  initialFocus,
}: KoobModalProps) {
  const close = () => {
    if (setOpen && allowClose) {
      setOpen(false);
    }
  };

  const noFocusRef = useRef<HTMLDivElement>(null);
  const resolvedInitialFocus =
    initialFocus ?? (autoFocus ? undefined : noFocusRef);

  return (
    // Portaled, but React still bubbles the modal's clicks to whatever
    // rendered it (a clickable card). The stop cannot live inside the Dialog:
    // on an outside click Headless UI closes it from a document listener, and
    // React 17 unmounts it before handling that same click, so React starts
    // from the nearest mounted ancestor and skips every handler inside. This
    // span stays mounted; `contents` keeps it out of the opener's layout.
    <span className="contents" onClick={stopPropagation}>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-modal"
          open={open}
          onClose={() => close()}
          initialFocus={resolvedInitialFocus}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-modal overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel
                  className={`relative transform ${overflow === 'hidden' ? 'overflow-hidden' : 'overflow-visible'} rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full ${widthClass} ${paddingClass}`}
                >
                  {!autoFocus && !initialFocus && (
                    <div
                      ref={noFocusRef}
                      tabIndex={-1}
                      className="sr-only focus:outline-none"
                      aria-hidden="true"
                    />
                  )}
                  {showCloseIcon && allowClose && (
                    <button
                      type="button"
                      onClick={close}
                      aria-label="Close"
                      className="absolute z-10 top-4 right-4 sm:top-6 sm:right-6 border-none bg-transparent cursor-pointer text-gray-400 hover:text-gray-600"
                    >
                      <i className={icons.close} />
                    </button>
                  )}
                  {title && (
                    <h2 className="mb-4 pr-8 text-xl font-semibold text-gray-500">
                      {title}
                    </h2>
                  )}
                  {children}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </span>
  );
}
